import React, { useState, useRef, useEffect } from 'react';
import { 
  ArrowLeft, 
  TrendingUp, 
  Target, 
  CheckCircle, 
  AlertTriangle, 
  Lightbulb,
  Share2,
  BarChart3,
  Download,
  Zap,
  Star,
  Play,
  Pause,
  RotateCcw,
  Film,
  FileText,
  Quote,
  Eye,
  ThumbsUp,
  MessageCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import VideoPreview from './VideoPreview';

const StandardAnalysisResults = ({ data, onNewReview }) => {
  const [activeTab, setActiveTab] = useState('article');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const reportRef = useRef(null);
  const articleRef = useRef(null);
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  // Add comprehensive null safety checks
  if (!data || !data.review) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="card text-center">
          <div className="mb-4">
            <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Analysis Data Available</h2>
            <p className="text-gray-600 mb-6">
              The review data is missing or incomplete. Please try analyzing your video again.
            </p>
            <button
              onClick={onNewReview}
              className="btn-primary"
            >
              Start New Analysis
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  const { review, platform, platformInfo, generatedAt } = data;
  
  
  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-600 bg-green-100';
    if (score >= 5) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreBarColor = (score) => {
    if (score >= 8) return 'bg-green-500';
    if (score >= 5) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Enhanced 3-Act Structure Analysis
  const getThreeActAnalysis = () => {
    const criteria = review.criteria || review.videoAnalysis?.criteria || [];
    const threeActData = review.threeActAnalysis || {};
    
    // Act 1 - Setup (0-25% of video) - Opening Hook & Context
    const setupCriteria = criteria.filter(c => {
      const name = c.name.toLowerCase();
      return name.includes('hook') || name.includes('opening') || 
             name.includes('introduction') || name.includes('setup') || 
             name.includes('establish') || name.includes('introduce') ||
             name.includes('attention') || name.includes('first') || 
             name.includes('beginning') || name.includes('start');
    });
    
    // Act 2 - Confrontation (25-75% of video) - Problem & Tension
    const confrontationCriteria = criteria.filter(c => {
      const name = c.name.toLowerCase();
      return name.includes('conflict') || name.includes('tension') || 
             name.includes('challenge') || name.includes('problem') || 
             name.includes('struggle') || name.includes('obstacle') || 
             name.includes('crisis') || name.includes('climax') ||
             name.includes('engagement') || name.includes('interest') ||
             name.includes('desire') || name.includes('agitate');
    });
    
    // Act 3 - Resolution (75-100% of video) - Solution & CTA
    const resolutionCriteria = criteria.filter(c => {
      const name = c.name.toLowerCase();
      return name.includes('resolution') || name.includes('conclusion') || 
             name.includes('ending') || name.includes('solution') || 
             name.includes('outcome') || name.includes('result') || 
             name.includes('transformation') || name.includes('change') ||
             name.includes('action') || name.includes('cta') || 
             name.includes('call') || name.includes('close');
    });
    
    // Calculate scores for each act with more sophisticated logic
    const setupScore = setupCriteria.length > 0 
      ? Math.round((setupCriteria.reduce((sum, c) => sum + (c.score || 5), 0) / setupCriteria.length) * 10) / 10
      : 5.0; // Default fallback score
    
    const confrontationScore = confrontationCriteria.length > 0 
      ? Math.round((confrontationCriteria.reduce((sum, c) => sum + (c.score || 5), 0) / confrontationCriteria.length) * 10) / 10
      : 5.0; // Default fallback score
    
    const resolutionScore = resolutionCriteria.length > 0 
      ? Math.round((resolutionCriteria.reduce((sum, c) => sum + (c.score || 5), 0) / resolutionCriteria.length) * 10) / 10
      : 5.0; // Default fallback score
    
    const overallThreeActScore = Math.round(((setupScore + confrontationScore + resolutionScore) / 3) * 10) / 10;
    
    // Generate insights based on score patterns
    const generateInsights = () => {
      const insights = [];
      
      if (setupScore >= 8) {
        insights.push("Strong opening hook effectively captures attention");
      } else if (setupScore < 5) {
        insights.push("Opening hook needs improvement to capture attention faster");
      }
      
      if (confrontationScore >= 8) {
        insights.push("Excellent conflict development maintains engagement");
      } else if (confrontationScore < 5) {
        insights.push("Build more tension and conflict to keep viewers engaged");
      }
      
      if (resolutionScore >= 8) {
        insights.push("Clear resolution with effective call-to-action");
      } else if (resolutionScore < 5) {
        insights.push("Strengthen the resolution and clarify the call-to-action");
      }
      
      // Check for narrative arc balance
      if (Math.abs(setupScore - resolutionScore) <= 2) {
        insights.push("Well-balanced narrative arc from start to finish");
      }
      
      if (confrontationScore > setupScore && confrontationScore > resolutionScore) {
        insights.push("Strong middle section drives the narrative");
      }
      
      return insights;
    };
    
    return {
      setup: { 
        score: setupScore, 
        criteria: setupCriteria,
        text: threeActData.setup || "Character and context introduction",
        strength: setupScore >= 8 ? 'strong' : setupScore >= 5 ? 'moderate' : 'weak'
      },
      confrontation: { 
        score: confrontationScore, 
        criteria: confrontationCriteria,
        text: threeActData.confrontation || "Problem and conflict presentation",
        strength: confrontationScore >= 8 ? 'strong' : confrontationScore >= 5 ? 'moderate' : 'weak'
      },
      resolution: { 
        score: resolutionScore, 
        criteria: resolutionCriteria,
        text: threeActData.resolution || "Solution and product positioning",
        strength: resolutionScore >= 8 ? 'strong' : resolutionScore >= 5 ? 'moderate' : 'weak'
      },
      overall: overallThreeActScore,
      insights: generateInsights()
    };
  };

  // Enhanced Best Practices Analysis
  const getBestPracticesAnalysis = () => {
    const criteria = review.criteria || review.videoAnalysis?.criteria || [];
    const strengths = review.strengths || [];
    const improvements = review.improvements || [];
    const recommendations = review.recommendations || [];
    
    // Visual Quality - Production value, aesthetics, composition
    const visualCriteria = criteria.filter(c => {
      const name = c.name.toLowerCase();
      return name.includes('visual') || name.includes('lighting') || 
             name.includes('camera') || name.includes('composition') || 
             name.includes('aesthetic') || name.includes('color') ||
             name.includes('quality') || name.includes('production') ||
             name.includes('cinematography') || name.includes('filming');
    });
    
    // Audio Quality - Sound, voice, music
    const audioCriteria = criteria.filter(c => {
      const name = c.name.toLowerCase();
      return name.includes('audio') || name.includes('sound') || 
             name.includes('voice') || name.includes('music') || 
             name.includes('clarity') || name.includes('volume') ||
             name.includes('noise') || name.includes('soundtrack');
    });
    
    // Engagement - Hooks, pacing, storytelling
    const engagementCriteria = criteria.filter(c => {
      const name = c.name.toLowerCase();
      return name.includes('engagement') || name.includes('interaction') || 
             name.includes('attention') || name.includes('retention') || 
             name.includes('interest') || name.includes('hook') ||
             name.includes('pacing') || name.includes('compelling') ||
             name.includes('captivating') || name.includes('entertaining');
    });
    
    // Call to Action - CTA clarity and effectiveness
    const ctaCriteria = criteria.filter(c => {
      const name = c.name.toLowerCase();
      return name.includes('call') || name.includes('action') || 
             name.includes('cta') || name.includes('directive') ||
             name.includes('instruction') || name.includes('conversion') ||
             name.includes('next step') || name.includes('follow');
    });
    
    // Calculate scores with fallback logic
    const visualScore = visualCriteria.length > 0 
      ? Math.round((visualCriteria.reduce((sum, c) => sum + (c.score || 5), 0) / visualCriteria.length) * 10) / 10
      : 5.0; // Default fallback score
    
    const audioScore = audioCriteria.length > 0 
      ? Math.round((audioCriteria.reduce((sum, c) => sum + (c.score || 5), 0) / audioCriteria.length) * 10) / 10
      : 5.0; // Default fallback score
    
    const engagementScore = engagementCriteria.length > 0 
      ? Math.round((engagementCriteria.reduce((sum, c) => sum + (c.score || 5), 0) / engagementCriteria.length) * 10) / 10
      : 5.0; // Default fallback score
    
    const ctaScore = ctaCriteria.length > 0 
      ? Math.round((ctaCriteria.reduce((sum, c) => sum + (c.score || 5), 0) / ctaCriteria.length) * 10) / 10
      : 5.0; // Default fallback score
    
    const overallBestPracticesScore = Math.round(((visualScore + audioScore + engagementScore + ctaScore) / 4) * 10) / 10;
    
    // Generate actionable insights
    const generateActionableInsights = () => {
      const insights = {
        visual: [],
        audio: [],
        engagement: [],
        cta: []
      };
      
      // Visual insights
      if (visualScore >= 8) {
        insights.visual.push("Professional production quality enhances credibility");
      } else if (visualScore < 5) {
        insights.visual.push("Improve lighting and composition for better visual appeal");
        insights.visual.push("Consider investing in better equipment or editing");
      }
      
      // Audio insights
      if (audioScore >= 8) {
        insights.audio.push("Clear audio enhances message delivery");
      } else if (audioScore < 5) {
        insights.audio.push("Improve audio clarity with better microphone or post-processing");
        insights.audio.push("Balance background music with voice-over");
      }
      
      // Engagement insights
      if (engagementScore >= 8) {
        insights.engagement.push("Strong engagement elements keep viewers watching");
      } else if (engagementScore < 5) {
        insights.engagement.push("Add more dynamic elements to maintain viewer interest");
        insights.engagement.push("Strengthen the hook in first 3 seconds");
      }
      
      // CTA insights
      if (ctaScore >= 8) {
        insights.cta.push("Clear call-to-action drives desired behavior");
      } else if (ctaScore < 5) {
        insights.cta.push("Make your call-to-action more prominent and specific");
        insights.cta.push("Consider adding urgency or incentive to the CTA");
      }
      
      return insights;
    };
    
    const actionableInsights = generateActionableInsights();
    
    return {
      visual: { 
        score: visualScore, 
        criteria: visualCriteria,
        insights: actionableInsights.visual,
        grade: visualScore >= 8 ? 'A' : visualScore >= 6 ? 'B' : visualScore >= 4 ? 'C' : 'D'
      },
      audio: { 
        score: audioScore, 
        criteria: audioCriteria,
        insights: actionableInsights.audio,
        grade: audioScore >= 8 ? 'A' : audioScore >= 6 ? 'B' : audioScore >= 4 ? 'C' : 'D'
      },
      engagement: { 
        score: engagementScore, 
        criteria: engagementCriteria,
        insights: actionableInsights.engagement,
        grade: engagementScore >= 8 ? 'A' : engagementScore >= 6 ? 'B' : engagementScore >= 4 ? 'C' : 'D'
      },
      cta: { 
        score: ctaScore, 
        criteria: ctaCriteria,
        insights: actionableInsights.cta,
        grade: ctaScore >= 8 ? 'A' : ctaScore >= 6 ? 'B' : ctaScore >= 4 ? 'C' : 'D'
      },
      overall: overallBestPracticesScore,
      strengths: strengths.slice(0, 3),
      improvements: improvements.slice(0, 3),
      recommendations: recommendations.slice(0, 3)
    };
  };

  // Calculate overall score as average of 3-act structure and best practices scores
  const threeActAnalysis = getThreeActAnalysis();
  const bestPracticesAnalysis = getBestPracticesAnalysis();
  const overallScore = Math.round(((threeActAnalysis.overall + bestPracticesAnalysis.overall) / 2) * 10) / 10;

  // Dynamic AI Content Generation Functions
  const generateDynamicHeadline = () => {
    const score = overallScore;
    const strengths = review.strengths || [];
    const improvements = review.improvements || [];
    
    if (score >= 8) {
      const topStrength = strengths[0]?.toLowerCase() || '';
      if (topStrength.includes('visual') || topStrength.includes('quality')) {
        return "Visual Excellence Meets Strategic Storytelling: A Masterclass in Video Marketing";
      } else if (topStrength.includes('engagement') || topStrength.includes('hook')) {
        return "The Hook That Converts: How This Video Captures and Retains Attention";
      } else if (topStrength.includes('story') || topStrength.includes('narrative')) {
        return "Storytelling That Sells: The Anatomy of a High-Performing Video";
      } else {
        return "What Makes This Video Work: A Deep Dive Into Marketing Excellence";
      }
    } else if (score >= 6) {
      return "Unlocking Your Video's Hidden Potential: From Good to Great";
    } else if (score >= 4) {
      return "The Path to Video Marketing Success: Your Optimization Roadmap";
    } else {
      return "Transforming Your Video Strategy: From Underperforming to Outstanding";
    }
  };

  const generateDynamicLeadParagraph = () => {
    const score = overallScore;
    const setupScore = threeActAnalysis.setup.score;
    const confrontationScore = threeActAnalysis.confrontation.score;
    const resolutionScore = threeActAnalysis.resolution.score;
    
    const narrativeStrength = Math.max(setupScore, confrontationScore, resolutionScore);
    const weakestArea = Math.min(setupScore, confrontationScore, resolutionScore);
    
    let narrativeAssessment = '';
    if (narrativeStrength >= 8) {
      narrativeAssessment = 'demonstrates exceptional narrative craftsmanship';
    } else if (narrativeStrength >= 6) {
      narrativeAssessment = 'shows solid storytelling fundamentals';
    } else {
      narrativeAssessment = 'presents opportunities for narrative enhancement';
    }
    
    if (score >= 8) {
      return `In the competitive landscape of social media marketing, standing out requires more than just good content—it demands strategic storytelling that resonates with your audience. This video analysis reveals a content piece that ${narrativeAssessment}, achieving an impressive ${overallScore}/10 overall score. Through our comprehensive evaluation of narrative structure, visual quality, and engagement tactics, we uncover the specific elements that make this video effective and the strategic insights that can elevate your entire marketing approach.`;
    } else if (score >= 6) {
      return `Every video tells a story, but not every story converts. This analysis reveals a content piece with ${narrativeAssessment} and an overall score of ${overallScore}/10. While the foundation shows promise, there are specific areas where strategic improvements can transform viewer engagement and drive measurable results. We'll explore how to strengthen the narrative flow, enhance visual impact, and optimize for maximum conversion potential.`;
    } else {
      return `Video marketing success isn't about perfection—it's about strategic improvement. With an overall score of ${overallScore}/10, this analysis identifies the key areas where targeted enhancements can significantly boost performance. By understanding where the narrative falters and how to strengthen the weakest elements, we can create a clear roadmap for transforming this content into a high-converting marketing asset.`;
    }
  };

  const generateDynamicSectionIntro = (section, score, strength) => {
    const sectionIntros = {
      setup: {
        high: `The opening moments of any video are make-or-break for audience retention. With a ${score}/10 score in the setup phase, this video ${strength === 'strong' ? 'demonstrates masterful audience engagement from the very first frame' : strength === 'moderate' ? 'shows solid foundational work with room for strategic enhancement' : 'presents significant opportunities for improving initial viewer capture'}.`,
        medium: `First impressions matter more in video marketing than in any other medium. The setup phase scored ${score}/10, indicating ${strength === 'strong' ? 'strong potential for audience retention' : strength === 'moderate' ? 'moderate effectiveness that can be optimized' : 'critical areas for improvement in viewer engagement'}.`,
        low: `The opening of your video sets the tone for everything that follows. With a ${score}/10 setup score, this analysis reveals ${strength === 'strong' ? 'effective opening strategies that can be leveraged' : strength === 'moderate' ? 'foundational elements that need strengthening' : 'fundamental issues that must be addressed to improve performance'}.`
      },
      confrontation: {
        high: `The middle section is where most videos either captivate or lose their audience. Scoring ${score}/10 in the confrontation phase, this video ${strength === 'strong' ? 'maintains exceptional momentum through strategic tension building' : strength === 'moderate' ? 'shows competent engagement tactics with potential for enhancement' : 'struggles to maintain viewer interest during the critical middle section'}.`,
        medium: `The confrontation phase is the make-or-break moment for audience retention. With a ${score}/10 score, this section ${strength === 'strong' ? 'effectively builds tension and maintains engagement' : strength === 'moderate' ? 'demonstrates basic engagement principles with room for growth' : 'requires significant restructuring to improve viewer retention'}.`,
        low: `The middle act determines whether viewers stay engaged or abandon ship. This ${score}/10 confrontation score reveals ${strength === 'strong' ? 'promising elements that can be amplified' : strength === 'moderate' ? 'foundational issues that need immediate attention' : 'critical weaknesses that are likely causing audience drop-off'}.`
      },
      resolution: {
        high: `The resolution phase is where viewer engagement transforms into action. Scoring ${score}/10, this video ${strength === 'strong' ? 'delivers a compelling conclusion that drives viewer behavior' : strength === 'moderate' ? 'provides adequate closure with opportunities for stronger calls-to-action' : 'struggles to convert viewer interest into desired outcomes'}.`,
        medium: `The final act determines whether your video achieves its marketing objectives. With a ${score}/10 resolution score, this section ${strength === 'strong' ? 'effectively guides viewers toward desired actions' : strength === 'moderate' ? 'shows basic conversion elements that need strengthening' : 'lacks the clarity needed to drive meaningful viewer response'}.`,
        low: `The resolution is your last chance to convert viewers into customers. This ${score}/10 score indicates ${strength === 'strong' ? 'elements that work but need amplification' : strength === 'moderate' ? 'fundamental issues with the closing strategy' : 'critical problems that are preventing conversion success'}.`
      }
    };

    const scoreCategory = score >= 8 ? 'high' : score >= 5 ? 'medium' : 'low';
    return sectionIntros[section]?.[scoreCategory] || sectionIntros[section]?.medium || '';
  };

  const generateDynamicInsights = (criteria, section) => {
    if (!criteria || criteria.length === 0) return [];
    
    return criteria.slice(0, 3).map((criterion, index) => {
      const score = criterion.score;
      let insight = '';
      
      if (score >= 8) {
        insight = `Exceptional performance in ${criterion.name.toLowerCase()}. ${criterion.explanation}`;
      } else if (score >= 6) {
        insight = `Solid execution of ${criterion.name.toLowerCase()}. ${criterion.explanation}`;
      } else if (score >= 4) {
        insight = `${criterion.name} shows potential but needs refinement. ${criterion.explanation}`;
      } else {
        insight = `Critical improvement needed in ${criterion.name.toLowerCase()}. ${criterion.explanation}`;
      }
      
      return {
        ...criterion,
        dynamicInsight: insight
      };
    });
  };

  const generateDynamicConclusion = () => {
    const score = overallScore;
    const strengths = review.strengths || [];
    const improvements = review.improvements || [];
    const topStrength = strengths[0]?.toLowerCase() || '';
    const primaryImprovement = improvements[0]?.toLowerCase() || '';
    
    if (score >= 8) {
      return {
        verdict: "Exceptional Performance",
        message: `This video represents a masterclass in strategic video marketing. With an overall score of ${overallScore}/10, it demonstrates ${topStrength.includes('visual') ? 'exceptional visual storytelling' : topStrength.includes('engagement') ? 'outstanding audience engagement tactics' : 'superior marketing strategy execution'}. The combination of strong narrative structure, effective visual communication, and strategic positioning creates a compelling case for continued investment in this approach.`,
        recommendation: "This content is ready for scale. Consider creating similar videos following the same strategic framework to maximize ROI across your marketing channels."
      };
    } else if (score >= 6) {
      return {
        verdict: "Strong Foundation with Growth Potential",
        message: `Your video shows solid marketing fundamentals with an overall score of ${overallScore}/10. The ${topStrength || 'core strengths'} provide a strong foundation for building more effective content. By addressing the ${primaryImprovement || 'identified improvement areas'}, you can significantly enhance performance and drive better results.`,
        recommendation: "Focus on the highest-impact improvements first. Small changes in the weakest areas can yield substantial performance gains."
      };
    } else {
      return {
        verdict: "Significant Improvement Opportunity",
        message: `With an overall score of ${overallScore}/10, this video presents clear opportunities for strategic enhancement. The analysis reveals specific areas where targeted improvements can transform performance. ${primaryImprovement ? `Focus on ${primaryImprovement} as the primary optimization target.` : 'Prioritize narrative structure and visual quality improvements.'}`,
        recommendation: "Implement the recommended changes systematically. Consider A/B testing different approaches to find what resonates best with your audience."
      };
    }
  };

  const generatePDF = async () => {
    // Check if article content is available
    if (!articleRef.current) {
      toast.error('Article content not available');
      return;
    }
    
    setIsGeneratingPDF(true);
    try {
      // Enhanced html2canvas options for production
      const canvas = await html2canvas(articleRef.current, {
        scale: 1.5, // Reduced scale for better compatibility
        useCORS: true,
        allowTaint: false, // Changed to false for better security
        backgroundColor: '#ffffff',
        logging: false,
        width: articleRef.current.scrollWidth,
        height: articleRef.current.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        ignoreElements: (element) => {
          // Ignore video elements that might cause issues
          return element.tagName === 'VIDEO' || element.classList.contains('video-element');
        }
      });
      
      const imgData = canvas.toDataURL('image/png', 0.8); // Reduced quality for better compatibility
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      pdf.save(`video-analysis-article-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF article generated successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF report. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'article', label: 'Analysis', icon: FileText },
    { id: 'bestpractices', label: 'Best Practices', icon: CheckCircle }
  ];

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={onNewReview}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white rounded-lg hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">New Analysis</span>
          </button>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={generatePDF}
              disabled={isGeneratingPDF}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              <span>{isGeneratingPDF ? 'Generating...' : 'Download Article PDF'}</span>
            </button>
          </div>
        </div>

        {/* Overall Score Card */}
        <div className="relative">
          {/* Glowing background effect */}
          <div className="absolute -inset-4 bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 rounded-3xl blur-xl opacity-20 animate-pulse"></div>
          <div className="absolute -inset-2 bg-gradient-to-r from-purple-300 via-pink-300 to-blue-300 rounded-2xl blur-lg opacity-30"></div>
          
          {/* Main card container */}
          <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden backdrop-blur-sm">
            {/* Gradient Header */}
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-3 md:p-4 text-center relative">
              <div className="absolute inset-0 bg-black opacity-10"></div>
              <div className="relative z-10">
                <h1 className="text-base sm:text-lg md:text-xl font-bold text-white mb-1">Standard Video Analysis</h1>
                <p className="text-indigo-100 text-xs sm:text-sm">Essential storytelling and best practices evaluation</p>
              </div>
              {/* Decorative elements */}
              <div className="absolute top-2 left-2 w-1.5 h-1.5 bg-white bg-opacity-30 rounded-full"></div>
              <div className="absolute top-4 right-4 w-1 h-1 bg-white bg-opacity-40 rounded-full"></div>
              <div className="absolute bottom-3 left-6 w-1 h-1 bg-white bg-opacity-30 rounded-full"></div>
              <div className="absolute bottom-2 right-2 w-1.5 h-1.5 bg-white bg-opacity-20 rounded-full"></div>
            </div>
            
            <div className="p-3 md:p-4">
              <div className="flex flex-col md:flex-row items-center justify-between gap-3 md:gap-4">
                {/* First Frame - Left Side */}
                {data.videoFile && (
                  <div className="flex-shrink-0 w-24 sm:w-32 md:w-36 hidden md:block">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-2 md:p-3 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                      <div className="relative group">
                        <div className="relative rounded-lg overflow-hidden shadow-md bg-black">
                          <video 
                            className="w-full h-auto"
                            preload="metadata"
                            muted
                            playsInline
                            onLoadedMetadata={(e) => {
                              e.target.currentTime = 0.001;
                            }}
                            onMouseEnter={(e) => {
                              e.target.play().catch(() => {});
                            }}
                            onMouseLeave={(e) => {
                              e.target.pause();
                              e.target.currentTime = 0.001;
                            }}
                          >
                            <source src={`/uploads/${data.videoFile.filename}`} type={data.videoFile.mimetype} />
                          </video>
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center pointer-events-none">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <div className="w-8 h-8 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                                <Play className="w-4 h-4 text-gray-700 ml-0.5" />
                              </div>
                            </div>
                          </div>
                          <div className="absolute top-1 right-1 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded">
                            0:00
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Scores - Center */}
                <div className="flex items-center justify-center space-x-3 sm:space-x-4 md:space-x-8 flex-1">
                  <div className="text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full text-base sm:text-xl md:text-2xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 ${getScoreColor(overallScore)}`}>
                      {overallScore.toFixed(1)}/10
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 mt-2 md:mt-3 font-semibold">Overall</p>
                  </div>
                  
                  <div className="text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full text-base sm:text-xl md:text-2xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 ${getScoreColor(threeActAnalysis.overall)}`}>
                      {threeActAnalysis.overall.toFixed(1)}/10
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 mt-2 md:mt-3 font-semibold">3-Act</p>
                  </div>
                  
                  <div className="text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full text-base sm:text-xl md:text-2xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 ${getScoreColor(bestPracticesAnalysis.overall)}`}>
                      {bestPracticesAnalysis.overall.toFixed(1)}/10
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 mt-2 md:mt-3 font-semibold whitespace-nowrap">Best Practices</p>
                  </div>
                </div>
                
                {/* Last Frame - Right Side */}
                {data.videoFile && (
                  <div className="flex-shrink-0 w-24 sm:w-32 md:w-36 hidden md:block">
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-2 md:p-3 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                      <div className="relative group">
                        <div className="relative rounded-lg overflow-hidden shadow-md bg-black">
                          <video 
                            className="w-full h-auto"
                            preload="metadata"
                            muted
                            playsInline
                            onLoadedMetadata={(e) => {
                              const duration = e.target.duration;
                              if (duration && duration > 0) {
                                e.target.currentTime = Math.max(0, duration - 0.001);
                              }
                            }}
                            onMouseEnter={(e) => {
                              e.target.play().catch(() => {});
                            }}
                            onMouseLeave={(e) => {
                              e.target.pause();
                              const duration = e.target.duration;
                              if (duration && duration > 0) {
                                e.target.currentTime = Math.max(0, duration - 0.001);
                              }
                            }}
                          >
                            <source src={`/uploads/${data.videoFile.filename}`} type={data.videoFile.mimetype} />
                          </video>
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center pointer-events-none">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <div className="w-8 h-8 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                                <Play className="w-4 h-4 text-gray-700 ml-0.5" />
                              </div>
                            </div>
                          </div>
                          <div className="absolute top-1 right-1 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded">
                            End
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Strength Indicators */}
              <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-gray-200">
                <div className="grid grid-cols-3 gap-2 md:gap-4">
                  {/* Hook Strength */}
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-1 md:mb-2">
                      <div className={`w-2 h-2 md:w-3 md:h-3 rounded-full mr-1 md:mr-2 ${
                        threeActAnalysis.setup.score >= 8 ? 'bg-green-500' : 
                        threeActAnalysis.setup.score >= 5 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}></div>
                      <span className="text-[10px] sm:text-xs font-semibold text-gray-700">Hook Strength</span>
                    </div>
                    <div className={`text-xs sm:text-sm font-bold ${
                      threeActAnalysis.setup.score >= 8 ? 'text-green-600' : 
                      threeActAnalysis.setup.score >= 5 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {threeActAnalysis.setup.score >= 8 ? 'Strong' : threeActAnalysis.setup.score >= 5 ? 'Moderate' : 'Weak'}
                    </div>
                  </div>
                  
                  {/* Call to Action Strength */}
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-1 md:mb-2">
                      <div className={`w-2 h-2 md:w-3 md:h-3 rounded-full mr-1 md:mr-2 ${
                        threeActAnalysis.resolution.score >= 8 ? 'bg-green-500' : 
                        threeActAnalysis.resolution.score >= 5 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}></div>
                      <span className="text-[10px] sm:text-xs font-semibold text-gray-700">CTA Strength</span>
                    </div>
                    <div className={`text-xs sm:text-sm font-bold ${
                      threeActAnalysis.resolution.score >= 8 ? 'text-green-600' : 
                      threeActAnalysis.resolution.score >= 5 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {threeActAnalysis.resolution.score >= 8 ? 'Strong' : threeActAnalysis.resolution.score >= 5 ? 'Moderate' : 'Weak'}
                    </div>
                  </div>
                  
                  {/* Narrative Strength */}
                  <div className="text-center">
                    <div className="flex items-center justify-center mb-1 md:mb-2">
                      <div className={`w-2 h-2 md:w-3 md:h-3 rounded-full mr-1 md:mr-2 ${
                        threeActAnalysis.confrontation.score >= 8 ? 'bg-green-500' : 
                        threeActAnalysis.confrontation.score >= 5 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}></div>
                      <span className="text-[10px] sm:text-xs font-semibold text-gray-700">Narrative Strength</span>
                    </div>
                    <div className={`text-xs sm:text-sm font-bold ${
                      threeActAnalysis.confrontation.score >= 8 ? 'text-green-600' : 
                      threeActAnalysis.confrontation.score >= 5 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {threeActAnalysis.confrontation.score >= 8 ? 'Strong' : threeActAnalysis.confrontation.score >= 5 ? 'Moderate' : 'Weak'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="mb-8">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-md transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-white text-indigo-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div ref={reportRef} className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        {activeTab === 'overview' && (
          <div className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Analysis Overview</h2>
            
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* 3-Act Structure Summary */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <Play className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">3-Act Structure</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Setup</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${getScoreBarColor(threeActAnalysis.setup.score)}`}
                          style={{ width: `${(threeActAnalysis.setup.score / 10) * 100}%` }}
                        ></div>
                      </div>
                      <span className={`text-sm font-medium ${getScoreColor(threeActAnalysis.setup.score)}`}>
                        {threeActAnalysis.setup.score.toFixed(1)}/10
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Confrontation</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${getScoreBarColor(threeActAnalysis.confrontation.score)}`}
                          style={{ width: `${(threeActAnalysis.confrontation.score / 10) * 100}%` }}
                        ></div>
                      </div>
                      <span className={`text-sm font-medium ${getScoreColor(threeActAnalysis.confrontation.score)}`}>
                        {threeActAnalysis.confrontation.score.toFixed(1)}/10
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Resolution</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${getScoreBarColor(threeActAnalysis.resolution.score)}`}
                          style={{ width: `${(threeActAnalysis.resolution.score / 10) * 100}%` }}
                        ></div>
                      </div>
                      <span className={`text-sm font-medium ${getScoreColor(threeActAnalysis.resolution.score)}`}>
                        {threeActAnalysis.resolution.score.toFixed(1)}/10
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Best Practices Summary */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center mr-3">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Best Practices</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Visual Quality</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${getScoreBarColor(bestPracticesAnalysis.visual.score)}`}
                          style={{ width: `${(bestPracticesAnalysis.visual.score / 10) * 100}%` }}
                        ></div>
                      </div>
                      <span className={`text-sm font-medium ${getScoreColor(bestPracticesAnalysis.visual.score)}`}>
                        {bestPracticesAnalysis.visual.score.toFixed(1)}/10
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Audio Quality</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${getScoreBarColor(bestPracticesAnalysis.audio.score)}`}
                          style={{ width: `${(bestPracticesAnalysis.audio.score / 10) * 100}%` }}
                        ></div>
                      </div>
                      <span className={`text-sm font-medium ${getScoreColor(bestPracticesAnalysis.audio.score)}`}>
                        {bestPracticesAnalysis.audio.score.toFixed(1)}/10
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Engagement</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${getScoreBarColor(bestPracticesAnalysis.engagement.score)}`}
                          style={{ width: `${(bestPracticesAnalysis.engagement.score / 10) * 100}%` }}
                        ></div>
                      </div>
                      <span className={`text-sm font-medium ${getScoreColor(bestPracticesAnalysis.engagement.score)}`}>
                        {bestPracticesAnalysis.engagement.score.toFixed(1)}/10
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Call to Action</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${getScoreBarColor(bestPracticesAnalysis.cta.score)}`}
                          style={{ width: `${(bestPracticesAnalysis.cta.score / 10) * 100}%` }}
                        ></div>
                      </div>
                      <span className={`text-sm font-medium ${getScoreColor(bestPracticesAnalysis.cta.score)}`}>
                        {bestPracticesAnalysis.cta.score.toFixed(1)}/10
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Detailed 3-Act Structure Analysis */}
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">3-Act Structure Analysis</h2>
              
              {/* Key Insights */}
              {threeActAnalysis.insights && threeActAnalysis.insights.length > 0 && (
                <div className="mb-6 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                    <Lightbulb className="w-5 h-5 text-purple-600 mr-2" />
                    Key Narrative Insights
                  </h3>
                  <ul className="space-y-2">
                    {threeActAnalysis.insights.map((insight, index) => (
                      <li key={index} className="flex items-start text-gray-700">
                        <CheckCircle className="w-4 h-4 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{insight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="space-y-8">
                {/* Act 1: Setup */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold mr-3">
                        1
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Act 1: Setup (0-25%)</h3>
                        <span className={`text-xs font-medium ${
                          threeActAnalysis.setup.strength === 'strong' ? 'text-green-600' :
                          threeActAnalysis.setup.strength === 'moderate' ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {threeActAnalysis.setup.strength.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className={`px-4 py-2 rounded-full text-lg font-bold ${getScoreColor(threeActAnalysis.setup.score)}`}>
                      {threeActAnalysis.setup.score.toFixed(1)}/10
                    </div>
                  </div>
                  
                  {/* AI Analysis Text */}
                  <div className="mb-4 p-4 bg-white rounded-lg border border-blue-200">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      <span className="font-semibold text-blue-700">Analysis: </span>
                      {threeActAnalysis.setup.text}
                    </p>
                  </div>
                  
                  <p className="text-gray-600 mb-4">
                    <strong>Purpose:</strong> The opening establishes characters, setting, and introduces the main conflict or challenge. First impressions are critical for engagement.
                  </p>
                  
                  {threeActAnalysis.setup.criteria.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-900 mb-2">Related Criteria:</h4>
                      {threeActAnalysis.setup.criteria.map((criterion, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100">
                          <span className="text-gray-700">{criterion.name}</span>
                          <div className="flex items-center space-x-2">
                            <div className="w-20 bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${getScoreBarColor(criterion.score)}`}
                                style={{ width: `${(criterion.score / 10) * 100}%` }}
                              ></div>
                            </div>
                            <span className={`text-sm font-medium ${getScoreColor(criterion.score)}`}>
                              {criterion.score.toFixed(1)}/10
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Act 2: Confrontation */}
                <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6 border border-orange-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold mr-3">
                        2
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Act 2: Confrontation (25-75%)</h3>
                        <span className={`text-xs font-medium ${
                          threeActAnalysis.confrontation.strength === 'strong' ? 'text-green-600' :
                          threeActAnalysis.confrontation.strength === 'moderate' ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {threeActAnalysis.confrontation.strength.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className={`px-4 py-2 rounded-full text-lg font-bold ${getScoreColor(threeActAnalysis.confrontation.score)}`}>
                      {threeActAnalysis.confrontation.score.toFixed(1)}/10
                    </div>
                  </div>
                  
                  {/* AI Analysis Text */}
                  <div className="mb-4 p-4 bg-white rounded-lg border border-orange-200">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      <span className="font-semibold text-orange-700">Analysis: </span>
                      {threeActAnalysis.confrontation.text}
                    </p>
                  </div>
                  
                  <p className="text-gray-600 mb-4">
                    <strong>Purpose:</strong> The middle section develops tension, obstacles, and builds toward the climax. This is where engagement is maintained or lost.
                  </p>
                  
                  {threeActAnalysis.confrontation.criteria.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-900 mb-2">Related Criteria:</h4>
                      {threeActAnalysis.confrontation.criteria.map((criterion, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-orange-100">
                          <span className="text-gray-700">{criterion.name}</span>
                          <div className="flex items-center space-x-2">
                            <div className="w-20 bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${getScoreBarColor(criterion.score)}`}
                                style={{ width: `${(criterion.score / 10) * 100}%` }}
                              ></div>
                            </div>
                            <span className={`text-sm font-medium ${getScoreColor(criterion.score)}`}>
                              {criterion.score.toFixed(1)}/10
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Act 3: Resolution */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center font-bold mr-3">
                        3
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Act 3: Resolution (75-100%)</h3>
                        <span className={`text-xs font-medium ${
                          threeActAnalysis.resolution.strength === 'strong' ? 'text-green-600' :
                          threeActAnalysis.resolution.strength === 'moderate' ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {threeActAnalysis.resolution.strength.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className={`px-4 py-2 rounded-full text-lg font-bold ${getScoreColor(threeActAnalysis.resolution.score)}`}>
                      {threeActAnalysis.resolution.score.toFixed(1)}/10
                    </div>
                  </div>
                  
                  {/* AI Analysis Text */}
                  <div className="mb-4 p-4 bg-white rounded-lg border border-green-200">
                    <p className="text-sm text-gray-700 leading-relaxed">
                      <span className="font-semibold text-green-700">Analysis: </span>
                      {threeActAnalysis.resolution.text}
                    </p>
                  </div>
                  
                  <p className="text-gray-600 mb-4">
                    <strong>Purpose:</strong> The conclusion resolves the conflict and provides closure or transformation. This is where your call-to-action should be clear.
                  </p>
                  
                  {threeActAnalysis.resolution.criteria.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-900 mb-2">Related Criteria:</h4>
                      {threeActAnalysis.resolution.criteria.map((criterion, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-100">
                          <span className="text-gray-700">{criterion.name}</span>
                          <div className="flex items-center space-x-2">
                            <div className="w-20 bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${getScoreBarColor(criterion.score)}`}
                                style={{ width: `${(criterion.score / 10) * 100}%` }}
                              ></div>
                            </div>
                            <span className={`text-sm font-medium ${getScoreColor(criterion.score)}`}>
                              {criterion.score.toFixed(1)}/10
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'bestpractices' && (
          <div className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Best Practices Analysis</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Visual Quality */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-2xl mr-3 ${
                      bestPracticesAnalysis.visual.grade === 'A' ? 'bg-green-500 text-white' :
                      bestPracticesAnalysis.visual.grade === 'B' ? 'bg-blue-500 text-white' :
                      bestPracticesAnalysis.visual.grade === 'C' ? 'bg-yellow-500 text-white' : 'bg-red-500 text-white'
                    }`}>
                      {bestPracticesAnalysis.visual.grade}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Visual Quality</h3>
                      <p className="text-xs text-gray-500">Production Value</p>
                    </div>
                  </div>
                  <div className={`px-4 py-2 rounded-full text-lg font-bold ${getScoreColor(bestPracticesAnalysis.visual.score)}`}>
                    {bestPracticesAnalysis.visual.score.toFixed(1)}/10
                  </div>
                </div>
                <p className="text-gray-600 mb-4">
                  Lighting, composition, camera work, and overall visual appeal.
                </p>
                
                {/* Insights */}
                {bestPracticesAnalysis.visual.insights && bestPracticesAnalysis.visual.insights.length > 0 && (
                  <div className="mb-4 p-3 bg-white rounded-lg border border-purple-200">
                    <ul className="space-y-1">
                      {bestPracticesAnalysis.visual.insights.map((insight, index) => (
                        <li key={index} className="text-sm text-gray-700 flex items-start">
                          <span className="text-purple-600 mr-2">•</span>
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {bestPracticesAnalysis.visual.criteria.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2">Related Criteria:</h4>
                    {bestPracticesAnalysis.visual.criteria.map((criterion, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-100">
                        <span className="text-gray-700 text-sm">{criterion.name}</span>
                        <div className="flex items-center space-x-2">
                          <div className="w-16 bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${getScoreBarColor(criterion.score)}`}
                              style={{ width: `${(criterion.score / 10) * 100}%` }}
                            ></div>
                          </div>
                          <span className={`text-xs font-medium ${getScoreColor(criterion.score)}`}>
                            {criterion.score}/10
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Audio Quality */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Audio Quality</h3>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(bestPracticesAnalysis.audio.score)}`}>
                    {bestPracticesAnalysis.audio.score.toFixed(1)}/10
                  </div>
                </div>
                <p className="text-gray-600 mb-4">
                  Sound clarity, music integration, and audio-visual balance.
                </p>
                <div className="space-y-2">
                  {bestPracticesAnalysis.audio.criteria.map((criterion, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-100">
                      <span className="text-gray-700">{criterion.name}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${getScoreBarColor(criterion.score)}`}
                            style={{ width: `${(criterion.score / 10) * 100}%` }}
                          ></div>
                        </div>
                        <span className={`text-sm font-medium ${getScoreColor(criterion.score)}`}>
                          {criterion.score}/10
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Engagement */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Engagement</h3>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(bestPracticesAnalysis.engagement.score)}`}>
                    {bestPracticesAnalysis.engagement.score.toFixed(1)}/10
                  </div>
                </div>
                <p className="text-gray-600 mb-4">
                  Content that captures and maintains viewer attention.
                </p>
                <div className="space-y-2">
                  {bestPracticesAnalysis.engagement.criteria.map((criterion, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-100">
                      <span className="text-gray-700">{criterion.name}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${getScoreBarColor(criterion.score)}`}
                            style={{ width: `${(criterion.score / 10) * 100}%` }}
                          ></div>
                        </div>
                        <span className={`text-sm font-medium ${getScoreColor(criterion.score)}`}>
                          {criterion.score}/10
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Call to Action */}
              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border border-orange-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Call to Action</h3>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(bestPracticesAnalysis.cta.score)}`}>
                    {bestPracticesAnalysis.cta.score}/10
                  </div>
                </div>
                <p className="text-gray-600 mb-4">
                  Clear direction for what viewers should do next.
                </p>
                <div className="space-y-2">
                  {bestPracticesAnalysis.cta.criteria.map((criterion, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-orange-100">
                      <span className="text-gray-700">{criterion.name}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-20 bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${getScoreBarColor(criterion.score)}`}
                            style={{ width: `${(criterion.score / 10) * 100}%` }}
                          ></div>
                        </div>
                        <span className={`text-sm font-medium ${getScoreColor(criterion.score)}`}>
                          {criterion.score}/10
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Article content - always rendered for PDF export but hidden when not active */}
        <div ref={articleRef} className={`p-8 max-w-4xl mx-auto ${activeTab === 'article' ? 'block' : 'hidden'}`}>
            {/* Magazine Article Header */}
            <div className="mb-8 pb-6 border-b-2 border-gray-200">
              <div className="text-xs text-indigo-600 font-semibold mb-2 tracking-wider uppercase">
                Video Marketing Analysis
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
                {generateDynamicHeadline()}
              </h1>
              <div className="flex items-center text-gray-600 text-sm space-x-4">
                <div className="flex items-center">
                  <Eye className="w-4 h-4 mr-1" />
                  <span>Analysis Report</span>
                </div>
                <span>•</span>
                <div className="flex items-center">
                  <span>{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                </div>
                <span>•</span>
                <div className={`px-2 py-1 rounded text-xs font-semibold ${
                  overallScore >= 8 ? 'bg-green-100 text-green-700' :
                  overallScore >= 5 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                }`}>
                  {overallScore}/10 Score
                </div>
              </div>
            </div>

            {/* Hero Image - First Frame */}
            {data.videoFile && (
              <div className="mb-6 float-right ml-4 w-64">
                <div className="relative rounded-lg overflow-hidden shadow-md">
                  <video 
                    className="w-full h-auto"
                    preload="metadata"
                    muted
                    playsInline
                    onLoadedMetadata={(e) => {
                      e.target.currentTime = 0.001;
                    }}
                  >
                    <source src={`/uploads/${data.videoFile.filename}`} type={data.videoFile.mimetype} />
                  </video>
                  <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-2 py-1">
                    Opening Frame
                  </div>
                </div>
              </div>
            )}

            {/* Lead Paragraph */}
            <div className="prose prose-lg max-w-none mb-8">
              <p className="text-xl text-gray-700 leading-relaxed font-serif">
                {generateDynamicLeadParagraph()}
              </p>
            </div>

            {/* Pull Quote */}
            {review.strengths && review.strengths.length > 0 && (
              <div className="my-8 border-l-4 border-indigo-600 bg-indigo-50 p-6 rounded-r-lg">
                <Quote className="w-8 h-8 text-indigo-600 mb-2" />
                <p className="text-xl font-serif italic text-gray-800 leading-relaxed">
                  "{review.strengths[0]}"
                </p>
              </div>
            )}

            {/* Section 1: The Opening Act */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                The Opening Act: Setting the Stage
              </h2>
              <div className="h-1 w-20 bg-indigo-600 mb-6"></div>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                {generateDynamicSectionIntro('setup', threeActAnalysis.setup.score, threeActAnalysis.setup.strength)}
              </p>

              {/* Setup Analysis Text Box */}
              <div className="bg-gray-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-6">
                <h3 className="font-bold text-gray-900 mb-2">What We Found:</h3>
                <p className="text-gray-700 leading-relaxed">
                  {threeActAnalysis.setup.text}
                </p>
              </div>

              {/* Key Takeaways for Setup */}
              {threeActAnalysis.setup.criteria.length > 0 && (
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Lightbulb className="w-5 h-5 text-blue-600 mr-2" />
                    Strategic Insights:
                  </h4>
                  <ul className="space-y-2">
                    {generateDynamicInsights(threeActAnalysis.setup.criteria, 'setup').map((criterion, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-gray-900 font-medium">{criterion.name}: </span>
                          <span className="text-gray-700">{criterion.dynamicInsight}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Section 2: The Middle Act with Video Frame */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Building Tension: The Heart of the Story
              </h2>
              <div className="h-1 w-20 bg-orange-600 mb-6"></div>

              {/* Mid-point Frame */}
              {data.videoFile && (
                <div className="float-right ml-4 mb-4 w-48">
                  <div className="relative rounded-lg overflow-hidden shadow-md">
                    <video 
                      className="w-full h-auto"
                      preload="metadata"
                      muted
                      playsInline
                      onLoadedMetadata={(e) => {
                        const duration = e.target.duration;
                        if (duration && duration > 0) {
                          e.target.currentTime = duration / 2; // Mid-point
                        }
                      }}
                    >
                      <source src={`/uploads/${data.videoFile.filename}`} type={data.videoFile.mimetype} />
                    </video>
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-2 py-1">
                      Mid-point
                    </div>
                  </div>
                </div>
              )}
              
              <p className="text-gray-700 leading-relaxed mb-6">
                {generateDynamicSectionIntro('confrontation', threeActAnalysis.confrontation.score, threeActAnalysis.confrontation.strength)}
              </p>

              <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-lg mb-6">
                <h3 className="font-bold text-gray-900 mb-2">The Tension Points:</h3>
                <p className="text-gray-700 leading-relaxed">
                  {threeActAnalysis.confrontation.text}
                </p>
              </div>

              <blockquote className="text-lg italic text-gray-600 border-l-4 border-gray-300 pl-4 my-6">
                "The middle act is where most videos lose their audience. Your challenge is to build enough 
                tension and interest to carry viewers through to the payoff."
              </blockquote>
              
              <div className="clear-both"></div>
            </div>

            {/* Section 3: The Resolution */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                The Payoff: Delivering on Your Promise
              </h2>
              <div className="h-1 w-20 bg-green-600 mb-6"></div>

              {/* Last Frame */}
              {data.videoFile && (
                <div className="mb-6 float-left mr-4 w-48">
                  <div className="relative rounded-lg overflow-hidden shadow-md">
                    <video 
                      className="w-full h-auto"
                      preload="metadata"
                      muted
                      playsInline
                      onLoadedMetadata={(e) => {
                        const duration = e.target.duration;
                        if (duration && duration > 0) {
                          e.target.currentTime = Math.max(0, duration - 0.001);
                        }
                      }}
                    >
                      <source src={`/uploads/${data.videoFile.filename}`} type={data.videoFile.mimetype} />
                    </video>
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs px-2 py-1">
                      Final Frame
                    </div>
                  </div>
                </div>
              )}
              
              <p className="text-gray-700 leading-relaxed mb-6">
                {generateDynamicSectionIntro('resolution', threeActAnalysis.resolution.score, threeActAnalysis.resolution.strength)}
              </p>

              <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg mb-6">
                <h3 className="font-bold text-gray-900 mb-2">The Resolution:</h3>
                <p className="text-gray-700 leading-relaxed">
                  {threeActAnalysis.resolution.text}
                </p>
              </div>
              
              <div className="clear-both"></div>
            </div>

            {/* Visual Quality Deep Dive */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Production Excellence: The Visual Language
              </h2>
              <div className="h-1 w-20 bg-purple-600 mb-6"></div>

              <p className="text-gray-700 leading-relaxed mb-6">
                {bestPracticesAnalysis.visual.score >= 8 
                  ? `Exceptional visual storytelling sets this content apart. With a ${bestPracticesAnalysis.visual.score}/10 score (Grade: ${bestPracticesAnalysis.visual.grade}), the visual execution demonstrates professional-grade production values that enhance credibility and viewer engagement.`
                  : bestPracticesAnalysis.visual.score >= 6
                  ? `Visual quality shows solid fundamentals with a ${bestPracticesAnalysis.visual.score}/10 score (Grade: ${bestPracticesAnalysis.visual.grade}). The imagery effectively supports the narrative, though there are opportunities to elevate the visual impact for stronger viewer retention.`
                  : `Visual execution scored ${bestPracticesAnalysis.visual.score}/10 (Grade: ${bestPracticesAnalysis.visual.grade}), indicating significant opportunities for improvement. Enhancing visual quality can dramatically improve viewer engagement and brand perception.`
                }
              </p>

              {/* Visual Insights */}
              {bestPracticesAnalysis.visual.insights && bestPracticesAnalysis.visual.insights.length > 0 && (
                <div className="bg-purple-50 rounded-lg p-6 mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Visual Analysis:</h4>
                  <ul className="space-y-2">
                    {bestPracticesAnalysis.visual.insights.map((insight, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-purple-600 mr-2 font-bold">→</span>
                        <span className="text-gray-700">{insight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Engagement Metrics */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                The Engagement Factor
              </h2>
              <div className="h-1 w-20 bg-green-600 mb-6"></div>

              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg">
                  <div className="text-3xl font-bold text-indigo-600 mb-1">{bestPracticesAnalysis.visual.score}</div>
                  <div className="text-xs text-gray-600">Visual</div>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg">
                  <div className="text-3xl font-bold text-blue-600 mb-1">{bestPracticesAnalysis.audio.score}</div>
                  <div className="text-xs text-gray-600">Audio</div>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg">
                  <div className="text-3xl font-bold text-green-600 mb-1">{bestPracticesAnalysis.engagement.score}</div>
                  <div className="text-xs text-gray-600">Engagement</div>
                </div>
              </div>

              <p className="text-gray-700 leading-relaxed mb-6">
                {bestPracticesAnalysis.engagement.score >= 8
                  ? `Exceptional engagement mastery is evident throughout this content. With a ${bestPracticesAnalysis.engagement.score}/10 engagement score, the video demonstrates sophisticated techniques for capturing and retaining viewer attention, creating an immersive experience that drives meaningful interaction.`
                  : bestPracticesAnalysis.engagement.score >= 6
                  ? `Solid engagement fundamentals are present with a ${bestPracticesAnalysis.engagement.score}/10 score. The content shows competent audience retention tactics, though strategic enhancements could significantly boost viewer interaction and brand recall.`
                  : `Engagement scored ${bestPracticesAnalysis.engagement.score}/10, indicating critical opportunities for improvement. Strengthening viewer retention tactics can transform this content from forgettable to memorable, driving better marketing outcomes.`
                }
              </p>

              {bestPracticesAnalysis.engagement.insights && bestPracticesAnalysis.engagement.insights.length > 0 && (
                <div className="space-y-3">
                  {bestPracticesAnalysis.engagement.insights.map((insight, index) => (
                    <div key={index} className="flex items-start p-4 bg-green-50 rounded-lg">
                      <ThumbsUp className="w-5 h-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-700">{insight}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Strengths Section */}
            {review.strengths && review.strengths.length > 0 && (
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  What's Working: Your Competitive Advantages
                </h2>
                <div className="h-1 w-20 bg-green-600 mb-6"></div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {review.strengths.map((strength, index) => (
                    <div key={index} className="flex items-start p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold mr-3 flex-shrink-0">
                        {index + 1}
                      </div>
                      <p className="text-gray-700">{strength}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Opportunities for Improvement */}
            {review.improvements && review.improvements.length > 0 && (
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Growth Opportunities: Where to Focus Next
                </h2>
                <div className="h-1 w-20 bg-yellow-600 mb-6"></div>

                <p className="text-gray-700 leading-relaxed mb-6">
                  Even the best videos have room for improvement. Here's where small changes can yield significant results:
                </p>

                <div className="space-y-4">
                  {review.improvements.map((improvement, index) => (
                    <div key={index} className="p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg">
                      <div className="flex items-start">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                        <p className="text-gray-700">{improvement}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Expert Recommendations */}
            {review.recommendations && review.recommendations.length > 0 && (
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Next Steps
                </h2>
                <div className="h-1 w-20 bg-indigo-600 mb-6"></div>

                <p className="text-gray-700 leading-relaxed mb-6">
                  Based on our comprehensive analysis, here are the most impactful changes you can make:
                </p>

                <div className="space-y-4">
                  {review.recommendations.map((recommendation, index) => (
                    <div key={index} className="flex items-start p-5 bg-white border border-indigo-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                      <div className="w-8 h-8 bg-indigo-600 text-white rounded flex items-center justify-center font-bold mr-4 flex-shrink-0">
                        {index + 1}
                      </div>
                      <div>
                        <p className="text-gray-800 font-medium mb-1">Action Item #{index + 1}</p>
                        <p className="text-gray-700">{recommendation}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Call-to-Action Analysis */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                The Conversion Moment: Your Call-to-Action
              </h2>
              <div className="h-1 w-20 bg-orange-600 mb-6"></div>

              <p className="text-gray-700 leading-relaxed mb-6">
                A video without a clear CTA is a missed opportunity. Your call-to-action scored <strong className="text-orange-600">{bestPracticesAnalysis.cta.score}/10</strong>, 
                earning a grade of <strong>{bestPracticesAnalysis.cta.grade}</strong>.
              </p>

              {bestPracticesAnalysis.cta.insights && bestPracticesAnalysis.cta.insights.length > 0 && (
                <div className="bg-orange-50 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-900 mb-3">CTA Insights:</h4>
                  <ul className="space-y-2">
                    {bestPracticesAnalysis.cta.insights.map((insight, index) => (
                      <li key={index} className="flex items-start">
                        <MessageCircle className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{insight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Final Verdict */}
            <div className="mb-8 p-8 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-2xl border border-indigo-200">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
                {generateDynamicConclusion().verdict}
              </h2>
              <div className="h-1 w-20 bg-indigo-600 mb-6 mx-auto"></div>

              <div className="text-center mb-6">
                <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full text-2xl font-bold ${getScoreColor(overallScore)} border-4 ${
                  overallScore >= 8 ? 'border-green-600' :
                  overallScore >= 5 ? 'border-yellow-600' : 'border-red-600'
                }`}>
                  <span className="leading-none">{overallScore}/10</span>
                </div>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed text-center max-w-2xl mx-auto mb-6">
                {generateDynamicConclusion().message}
              </p>

              <div className="bg-white rounded-lg p-6 border border-indigo-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 text-center">Strategic Recommendation</h3>
                <p className="text-gray-700 leading-relaxed text-center">
                  {generateDynamicConclusion().recommendation}
                </p>
              </div>
            </div>

            {/* Platform-Specific Insights */}
            {review.platformSpecific && (
              <div className="mb-12 p-6 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <Target className="w-5 h-5 text-indigo-600 mr-2" />
                  Platform-Specific Insights
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {review.platformSpecific}
                </p>
              </div>
            )}

            {/* Audience Analysis */}
            {review.audienceAnalysis && (
              <div className="mb-12 p-6 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                  <Eye className="w-5 h-5 text-blue-600 mr-2" />
                  Target Audience Analysis
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {review.audienceAnalysis}
                </p>
              </div>
            )}

            {/* Closing Statement */}
            <div className="text-center py-8 border-t-2 border-gray-200">
              <p className="text-xs text-gray-400">
                {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          </div>
      </div>

      {/* Bottom New Analysis Button */}
      <div className="mt-8 text-center">
        <button
          onClick={onNewReview}
          className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white font-semibold rounded-xl hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Analyze Another Video</span>
        </button>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Share Analysis</h3>
            <p className="text-gray-600 mb-6">
              Share this analysis with others or save it for later reference.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  toast.success('Link copied to clipboard!');
                  setShowShareModal(false);
                }}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Copy Link
              </button>
              <button
                onClick={() => setShowShareModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StandardAnalysisResults;
